import { BottomSheetModal } from '@gorhom/bottom-sheet';
import type { BillSwitchEventPayload } from '@pinwheel/react-modal';
import {
  CardTypeEnum,
  PinwheelCardInput,
  PinwheelExperienceInput,
  Query,
} from '@varo/graphql-schema-types';
import backgroundTextureSunset from 'assets/images/vxl/component/productIntroScreen/background-texture-sunset.png';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import BillManagerErrorScreen from 'features/billManager/components/BillManagerErrorScreen/BillManagerErrorScreen';
import { logErrorEvent } from 'features/billManager/helpers/logErrorEvent';
import UnfreezeCardsBottomSheet from 'features/billSwitch/components/UnfreezeCardsBottomSheet/UnfreezeCardsBottomSheet';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BillManagerHowToQualifyScreenQuery from 'services/graphql/queries/BillManager/BillManagerHowToQualifyScreenQuery';
import BillManagerQualificationQuery from 'services/graphql/queries/BillManager/BillManagerQualificationQuery';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ListItemGroup from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { computeBillsTotal } from 'shared/helpers/computeBillsTotal/computeBillsTotal';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import { useFetchCardsWithLockStatus } from 'shared/hooks/useFetchCardsWithLockStatus/useFetchCardsWithLockStatus';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

const SOURCE_NAME = 'bill_manager_how_to_qualify';

const FALLBACK_FEATURE_ITEMS = [
  { key: 'calendar', iconKey: 'CALENDAR' },
  { key: 'cancel', iconKey: 'SPARKLE' },
  { key: 'alerts', iconKey: 'NOTIFICATION' },
] as const;

const BillManagerHowToQualifyScreen = () => {
  const { defaultTitle, defaultMessage, retryButtonTitle, doneButtonTitle } =
    useDefaultErrorStrings();

  const { t: translatedText } = useTranslation('billManager', {
    keyPrefix: 'howToQualify',
  });

  const { segmentClient } = useAnalyticsConfiguration();
  const { top } = useSafeAreaInsets();
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();
  const { setSnackBarMessage } = useContext(GlobalUIContext);
  const [isCheckingQualification, setIsCheckingQualification] = useState(false);

  const [fetchQualification] = useCancellableQuery<Query>(
    BillManagerQualificationQuery,
  );
  const router = useRouter();

  const [{ data: screenData, fetching: screenFetching, error: screenError }] =
    useQuery<Query>({
      query: BillManagerHowToQualifyScreenQuery,
      requestPolicy: 'cache-and-network',
    });

  const {
    cards,
    hasCards,
    hasLockedCards,
    analyticsMetadata: { isDebitLocked, isBelieveLocked },
    cardsError,
    cardsFetching,
    refetchCards,
  } = useFetchCardsWithLockStatus();

  const hasActiveBelieveCard = cards?.some(
    (card) => card.type === CardTypeEnum.ChargeCard,
  );

  const cardOptions = [
    PinwheelCardInput.Debit,
    ...(hasActiveBelieveCard ? [PinwheelCardInput.Believe] : []),
  ];

  const canLaunchPinwheel = hasCards && !hasLockedCards;

  const checkQualification = async (
    billSwitches?: BillSwitchEventPayload[],
  ) => {
    setIsCheckingQualification(true);
    try {
      const data = await fetchQualification(
        {},
        { requestPolicy: 'network-only' },
      );

      if (data.billManagerQualification?.qualified) {
        router.replace('/bill-manager/landing');
        return;
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }

      logErrorEvent({
        segmentClient,
        error_type: 'qualification_error',
        error_step: 'qualification_check',
        error_detail: {
          message: error instanceof Error ? error.message : String(error),
        },
      });
    } finally {
      setIsCheckingQualification(false);
    }

    if (billSwitches?.length) {
      const amount = computeBillsTotal(billSwitches);
      router.replace({
        pathname: '/bill-switch/funding',
        params: { amt: amount },
      });
    }
  };

  useViewDidFocusLogScreenAnalytics({
    name: 'Screen Viewed',
    properties: {
      screen_name: 'Bill Manager How To Qualify',
      category: 'bill_manager',
    },
  });

  const launchPinwheel = () => {
    if (!canLaunchPinwheel) {
      return;
    }
    initiatePinwheel({
      input: {
        experience: PinwheelExperienceInput.BillSwitch,
        cardOptions,
      },
      sourceName: SOURCE_NAME,
      onLinkTokenError: () => {
        logErrorEvent({
          segmentClient,
          error_type: 'link_token_error',
          error_step: 'pinwheel_launch',
        });
        setSnackBarMessage?.(translatedText('linkTokenError'));
      },
      onOutcome: ({ sessionState }) => {
        const { billSwitches } = sessionState ?? {};
        checkQualification(billSwitches);
      },
    });
  };

  const isLoading = cardsFetching || screenFetching || isCheckingQualification;
  const hasError = Boolean(cardsError) || Boolean(screenError);
  const errorTitle = cardsError?.title ?? defaultTitle;
  const errorMessage = cardsError?.message ?? defaultMessage;

  const content = screenData?.billManagerHowToQualifyScreen;
  const headlineText = content?.headlineText ?? translatedText('title');
  const subtitleText = content?.subtitleText ?? translatedText('subtitle');
  const sectionHeaderText =
    content?.sectionHeaderText ?? translatedText('sectionHeader');
  const finePrintText = content?.finePrintText ?? translatedText('finePrint');
  const primaryButtonText = content?.primaryButtonText ?? translatedText('cta');

  const featureItems = content?.featureItems
    ? content.featureItems.map((item) => ({
        primaryCopy: item.text,
        colorTheme: 'transparent' as const,
        leadingAccessory: {
          id: `item-${item.id}-icon`,
          name: VXLComponentId.ICON_AVATAR as const,
          props: {
            variant: 'MD',
            iconKey: item.icon,
            backgroundColor: 'transparent',
          },
        },
        showsChevron: false,
      }))
    : FALLBACK_FEATURE_ITEMS.map((item) => ({
        primaryCopy: translatedText(`featureBullets.${item.key}`),
        colorTheme: 'transparent' as const,
        leadingAccessory: {
          id: `item-${item.key}-icon`,
          name: VXLComponentId.ICON_AVATAR as const,
          props: {
            variant: 'MD',
            iconKey: item.iconKey,
            backgroundColor: 'transparent',
          },
        },
        showsChevron: false,
      }));

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
              transparent
            />
          ),
        }}
      />

      <ScreenTransitionOverlay
        isLoading={isLoading}
        LoadingComponent={
          <View style={{ paddingTop: top + theme.spacing.m }}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          hasError ? (
            <BillManagerErrorScreen
              messageHeadline={errorTitle}
              messageBody={errorMessage}
              primaryButton={{
                title: doneButtonTitle,
                onPress: navigateBack,
              }}
              secondaryButton={{
                title: retryButtonTitle,
                onPress: refetchCards,
              }}
            />
          ) : null
        }
      />

      {!isLoading ? (
        <ProductIntroScreen
          visual="BILL_MANAGER_A"
          headlineTextStyle="expressive"
          backgroundTexture={backgroundTextureSunset as ImageSourcePropType}
          headlineText={headlineText}
          topPadding={70}
          primaryButton={{
            label: primaryButtonText,
            onPress: () => {
              if (!canLaunchPinwheel) {
                bottomSheetRef?.current?.present();
              } else {
                launchPinwheel();
              }
            },
            isLoading: isPinwheelLoading,
            analyticsId: 'move_my_bills',
            customAttributes: {
              source_name: SOURCE_NAME,
              button_name: 'move_my_bills',
              category: 'bill_manager',
              debit_locked: isDebitLocked,
              believe_locked: isBelieveLocked,
            },
          }}
          bodyContent={
            <View style={styles.bodyContainer}>
              <View style={styles.bodyTextContainer}>
                <BodyBaseText numberOfLines={0}>{subtitleText}</BodyBaseText>
              </View>
              <View style={styles.textContainer}>
                <View style={styles.bodyTextContainer}>
                  <TitleSmText numberOfLines={0}>
                    {sectionHeaderText}
                  </TitleSmText>
                </View>
                <ListItemGroup
                  items={featureItems}
                  containerStyle={styles.listItemGroup}
                />
              </View>
              <View style={styles.bodyTextContainer}>
                <BodyXSText numberOfLines={0} style={styles.finePrintText}>
                  {finePrintText}
                </BodyXSText>
              </View>
            </View>
          }
        />
      ) : null}
      <UnfreezeCardsBottomSheet
        ref={bottomSheetRef}
        useBelieveLanguage={false}
        hasLockedCards={hasLockedCards}
        hasCards={hasCards}
        cards={cards}
        analyticsMetadata={{ source_name: SOURCE_NAME }}
        onSuccess={() => {
          bottomSheetRef?.current?.dismiss();
          launchPinwheel();
        }}
        onDismiss={() => bottomSheetRef?.current?.dismiss()}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bodyContainer: {
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
  textContainer: {
    backgroundColor: theme.color.background.surface,
    borderRadius: 4,
    marginHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  listItemGroup: {
    backgroundColor: theme.color.background.surface,
  },
  bodyTextContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  finePrintText: {
    color: theme.color.text.secondary,
  },
});

export default BillManagerHowToQualifyScreen;
